<script setup lang="ts">
import type { CreateItem } from '~~/src/composables/useNavigationData'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import { useCreateElemental } from '~~/src/composables/mutations/elemetal/useCreateElemental'
import { useCreateForm } from '~~/src/composables/useCreateForm'
import { elementalFactory } from '~~/src/utils/elementals'
import ElementalIconPicker from '@/components/Elemental/ElemetalIconPicker/index.vue'
import IconForm from '@/components/Icon/Form/index.vue'
import { getElementTypeColor } from '~~/src/composables/theme'

interface Props {
  createItems: CreateItem[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  creating: [elementalType: ElementalTypeEnum | 'form']
}>()

defineOptions({
  name: 'CreateMenuContent',
})

const router = useRouter()
const user = useUser()

// Composables for creating different element types
const { mutate: createElemental, isPending: isCreatingElemental } = useCreateElemental()
const { createTable, isPending: isCreatingTable } = useCreateTable()
const { createForm, isPending: isCreatingForm } = useCreateForm()

const isPending = computed(() => isCreatingElemental.value || isCreatingTable.value || isCreatingForm.value)

function getElementLabel(elementalType: ElementalTypeEnum | 'form'): string {
  const item = props.createItems.find(i => i.elementalType === elementalType)
  return item?.title || 'Element'
}

function getIconColors(elementalType: ElementalTypeEnum | 'form') {
  if (elementalType === 'form')
    return ['#21242C', '#F6F6FF']
  return getElementTypeColor(elementalType as ElementalTypeEnum)
}

async function handleCreateElement(elementalType: ElementalTypeEnum | 'form') {
  // Check authentication
  if (!user.value) {
    emit('close')
    return router.push(useGetLoginURL())
  }

  // Emit creating event
  emit('creating', elementalType)

  try {
    // Handle form creation
    if (elementalType === 'form') {
      createForm(() => {
        // Close drawer after form is created and opened in new tab
        emit('close')
      })
      return
    }

    // Handle table creation
    if (elementalType === ElementalTypeEnum.Table) {
      createTable()
      emit('close')
      return
    }

    // Handle other elemental types
    const label = getElementLabel(elementalType)
    const elementalData = elementalFactory(elementalType as ElementalTypeEnum, {
      title: `Untitled ${label}`,
    })
    const route = getElementalTypeRoute(elementalType as ElementalTypeEnum)

    createElemental({ elementalType: elementalType as ElementalTypeEnum, elemental: elementalData }, {
      onSuccess: (result) => {
        router.push(`/${route}/${result?.id}/edit`)
        emit('close')
      },
    })
  }
  catch (error) {
    console.error('Failed to create element:', error)
  }
}
</script>

<template>
  <div class="create-menu-content">
    <button
      v-for="item in createItems"
      :key="item.title"
      type="button"
      class="create-option"
      :disabled="isPending"
      @click="handleCreateElement(item.elementalType)"
    >
      <div
        class="option-icon"
        :style="{
          background: getIconColors(item.elementalType)[1],
          color: getIconColors(item.elementalType)[0],
        }"
      >
        <IconForm v-if="item.elementalType === 'form'" class="w-4 h-4" />
        <ElementalIconPicker v-else :type="item.elementalType as ElementalTypeEnum" class="w-4 h-4" />
      </div>
      <span class="option-label">{{ item.title }}</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.create-menu-content {
  @apply
    flex
    flex-col
    gap-1;
}

.create-option {
  @apply
    relative
    flex
    cursor-pointer
    items-center
    gap-3
    rounded-lg
    px-3
    py-2.5
    text-sm
    outline-none
    select-none
    transition-colors
    duration-200
    hover:bg-gray-100
    focus:bg-gray-100
    disabled:opacity-50
    disabled:cursor-not-allowed;
}

.option-icon {
  @apply
    flex
    items-center
    justify-center
    rounded-md
    shrink-0
    overflow-hidden;

  width: 28px;
  height: 28px;

  svg {
    @apply
      w-full
      h-full;
  }
}

.option-label {
  @apply
    text-sm
    font-medium
    text-gray-900;
}
</style>
